/**
 * FundingBudgetPage — Business Budgeting Dashboard
 * 
 * 9-tab financial intelligence dashboard:
 * Overview | Cashflow 12W | P&L | Daily P&L | Accounts | Inv. Finance | Schema | Backend API | React Hooks
 * 
 * Integrates with /app/backend/funding/ module for:
 * - Plaid bank connections
 * - AI transaction classification (Claude)
 * - 12-week cashflow projections
 * - Daily P&L generation
 * - Inventory-based financing assessment
 * 
 * Route: /dashboard/funding/budget
 */

import React, { useState, useEffect, useMemo } from 'react';
import { MerchantHeader } from '../components/MerchantHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, BarChart3, Wallet,
  Building2, CreditCard, PiggyBank, Package, AlertTriangle,
  CheckCircle, Clock, RefreshCw, Plus, ExternalLink, Shield,
  ArrowUpRight, ArrowDownRight, Layers, Code2, Database
} from 'lucide-react';
import apiClient from '../services/api';

// =============================================================================
// Mock Data (used until Plaid connects real bank data)
// =============================================================================

const MOCK_ACCOUNTS = [
  { _id: "acc_01", name: "Chase Business Checking", kind: "checking", bank: "JPMorgan Chase", bal: 91240.50, sync: "1m ago", ok: true },
  { _id: "acc_02", name: "Chase Business Savings", kind: "savings", bank: "JPMorgan Chase", bal: 38420.00, sync: "1m ago", ok: true },
  { _id: "acc_03", name: "Amex Business Platinum", kind: "credit", bank: "American Express", bal: -7832.40, sync: "4m ago", ok: true },
  { _id: "acc_04", name: "Mercury Operating", kind: "checking", bank: "Mercury", bal: 26100.00, sync: "12m ago", ok: true },
  { _id: "acc_05", name: "Brex Corporate Card", kind: "credit", bank: "Brex", bal: -3210.00, sync: "8m ago", ok: true },
];

const MOCK_PNL = {
  revenue: { total: 94200, lines: [{ name: "Product Sales", amt: 56100 }, { name: "Service Revenue", amt: 28400 }, { name: "Subscriptions / SaaS", amt: 9700 }] },
  cogs: { total: 33100, lines: [{ name: "Raw Materials", amt: 19400 }, { name: "Direct Labor", amt: 9200 }, { name: "Fulfillment & Shipping", amt: 4500 }] },
  opex: { total: 36400, lines: [{ name: "Payroll & Benefits", amt: 19800 }, { name: "Rent & Facilities", amt: 6400 }, { name: "Marketing & Ads", amt: 5600 }, { name: "Software & Infra", amt: 2900 }, { name: "Insurance & Legal", amt: 1700 }] },
};

const MOCK_INV = {
  total: 162000, eligible: 129600, turnover: 4.5,
  items: [
    { sku: "WDG-PRO-100", name: "Premium Widget", qty: 2600, val: 52000, turn: 5.4, ok: true },
    { sku: "KIT-STD-200", name: "Standard Kit", qty: 1900, val: 38000, turn: 4.1, ok: true },
    { sku: "BLK-SUP-300", name: "Bulk Supply Pack", qty: 3400, val: 27200, turn: 6.8, ok: true },
    { sku: "SEA-ITM-400", name: "Seasonal Item", qty: 700, val: 13300, turn: 1.6, ok: false },
    { sku: "ACC-BDL-500", name: "Accessory Bundle", qty: 5200, val: 31500, turn: 7.9, ok: true },
  ],
};

const MOCK_CF = [
  { wk: 1, label: "W1", date: "Oct 26", inflow: 13189, outflow: 11243, net: 1946, balance: 120646, projected: false },
  { wk: 2, label: "W2", date: "Nov 2", inflow: 14259, outflow: 9869, net: 4390, balance: 125036, projected: false },
  { wk: 3, label: "W3", date: "Nov 9", inflow: 14203, outflow: 13884, net: 319, balance: 125355, projected: false },
  { wk: 4, label: "W4", date: "Nov 16", inflow: 12600, outflow: 12161, net: 439, balance: 125794, projected: false },
  { wk: 5, label: "W5", date: "Nov 23", inflow: 13769, outflow: 11196, net: 2573, balance: 128367, projected: true },
  { wk: 6, label: "W6", date: "Nov 30", inflow: 12303, outflow: 9707, net: 2596, balance: 130963, projected: true },
  { wk: 7, label: "W7", date: "Dec 7", inflow: 13906, outflow: 10827, net: 3079, balance: 134042, projected: true },
  { wk: 8, label: "W8", date: "Dec 14", inflow: 10321, outflow: 13084, net: -2763, balance: 131279, projected: true },
  { wk: 9, label: "W9", date: "Dec 21", inflow: 13397, outflow: 10967, net: 2430, balance: 133709, projected: true },
  { wk: 10, label: "W10", date: "Dec 28", inflow: 10669, outflow: 13247, net: -2578, balance: 131131, projected: true },
  { wk: 11, label: "W11", date: "Jan 4", inflow: 14245, outflow: 10113, net: 4132, balance: 135263, projected: true },
  { wk: 12, label: "W12", date: "Jan 11", inflow: 14919, outflow: 10891, net: 4028, balance: 139291, projected: true },
];

const MOCK_DPL = [
  { date: "Oct 26", revenue: 2543, cogs: 1008, opex: 776, net: 759 },
  { date: "Oct 27", revenue: 2557, cogs: 1012, opex: 810, net: 735 },
  { date: "Oct 28", revenue: 2583, cogs: 1020, opex: 799, net: 764 },
  { date: "Oct 29", revenue: 2575, cogs: 1024, opex: 782, net: 769 },
  { date: "Oct 30", revenue: 2550, cogs: 1020, opex: 789, net: 741 },
  { date: "Nov 1", revenue: 2560, cogs: 1012, opex: 801, net: 747 },
  { date: "Nov 2", revenue: 3521, cogs: 1311, opex: 1072, net: 1138 },
  { date: "Nov 3", revenue: 3536, cogs: 1290, opex: 1063, net: 1183 },
  { date: "Nov 4", revenue: 3539, cogs: 1319, opex: 1051, net: 1169 },
  { date: "Nov 5", revenue: 3539, cogs: 1318, opex: 1055, net: 1166 },
];

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmtK = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : fmt(n);

// =============================================================================
// Sub-Components
// =============================================================================

const KPI = ({ label, value, sub, icon: Icon, trend, testId }) => (
  <Card data-testid={testId} className="bg-card border-border/80 rounded-xl">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold mt-1 tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              <span>{trend >= 0 ? '+' : ''}{trend}%</span>
            </div>
          )}
        </div>
        {Icon && <div className="p-2 rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>}
      </div>
    </CardContent>
  </Card>
);

const CashflowBar = ({ week, maxVal }) => {
  const inflowH = Math.max(2, (week.inflow / maxVal) * 60);
  const outflowH = Math.max(2, (week.outflow / maxVal) * 60);
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <div className="flex items-end gap-0.5" style={{ height: 64 }}>
        <div className="w-3 rounded-t bg-emerald-500/80" style={{ height: inflowH }} title={`In: ${fmt(week.inflow)}`} />
        <div className="w-3 rounded-t bg-red-400/70" style={{ height: outflowH }} title={`Out: ${fmt(week.outflow)}`} />
      </div>
      <span className={`text-[10px] ${week.projected ? 'text-muted-foreground/60 italic' : 'text-muted-foreground'}`}>{week.label}</span>
      <span className="text-[9px] text-muted-foreground/50">{week.date}</span>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export default function FundingBudgetPage() {
  const [tab, setTab] = useState('overview');
  const [connectModal, setConnectModal] = useState(false);

  // Derived metrics
  const totalBal = MOCK_ACCOUNTS.reduce((s, a) => s + a.bal, 0);
  const gp = MOCK_PNL.revenue.total - MOCK_PNL.cogs.total;
  const ni = gp - MOCK_PNL.opex.total;
  const weeklyBurn = MOCK_PNL.opex.total / 4;
  const runway = Math.floor(totalBal / weeklyBurn);
  const maxCF = Math.max(...MOCK_CF.map(w => Math.max(w.inflow, w.outflow)));

  return (
    <div className="min-h-screen bg-background">
      <MerchantHeader />
      
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="funding-budget-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Business Budgeting
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              Cashflow, P&L, bank accounts, and inventory financing — all powered by AI.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="connect-bank-btn" onClick={() => setConnectModal(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Connect Bank
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Sync
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} data-testid="budget-tabs">
          <TabsList className="bg-muted rounded-lg p-1 mb-6 flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cashflow">Cashflow 12W</TabsTrigger>
            <TabsTrigger value="pnl">P&L</TabsTrigger>
            <TabsTrigger value="daily">Daily P&L</TabsTrigger>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
            <TabsTrigger value="inventory">Inv. Finance</TabsTrigger>
          </TabsList>

          {/* ─── OVERVIEW ─── */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Total Balance" value={fmt(totalBal)} icon={Wallet} trend={3.2} testId="kpi-balance" />
              <KPI label="Monthly Revenue" value={fmt(MOCK_PNL.revenue.total)} icon={TrendingUp} trend={8.1} testId="kpi-revenue" />
              <KPI label="Net Income" value={fmt(ni)} sub={`${((ni / MOCK_PNL.revenue.total) * 100).toFixed(1)}% margin`} icon={DollarSign} testId="kpi-net-income" />
              <KPI label="Cash Runway" value={`${runway} weeks`} sub={`${fmtK(weeklyBurn)}/wk burn`} icon={Clock} testId="kpi-runway" />
            </div>

            {/* Mini Cashflow Chart */}
            <Card className="bg-card border-border/80 rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">12-Week Cashflow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 justify-between">
                  {MOCK_CF.map(w => <CashflowBar key={w.wk} week={w} maxVal={maxCF} />)}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-emerald-500/80" /> Inflow</span>
                  <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-red-400/70" /> Outflow</span>
                  <span className="italic ml-auto">Weeks 5-12 are AI-projected</span>
                </div>
              </CardContent>
            </Card>

            {/* P&L Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-card border-border/80 rounded-xl">
                <CardContent className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Revenue</p>
                  <p className="text-xl font-bold text-emerald-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(MOCK_PNL.revenue.total)}</p>
                  {MOCK_PNL.revenue.lines.map(l => (
                    <div key={l.name} className="flex justify-between text-xs mt-1.5 text-muted-foreground">
                      <span>{l.name}</span><span>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="bg-card border-border/80 rounded-xl">
                <CardContent className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">COGS</p>
                  <p className="text-xl font-bold text-amber-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(MOCK_PNL.cogs.total)}</p>
                  {MOCK_PNL.cogs.lines.map(l => (
                    <div key={l.name} className="flex justify-between text-xs mt-1.5 text-muted-foreground">
                      <span>{l.name}</span><span>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="bg-card border-border/80 rounded-xl">
                <CardContent className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Operating Expenses</p>
                  <p className="text-xl font-bold text-red-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(MOCK_PNL.opex.total)}</p>
                  {MOCK_PNL.opex.lines.map(l => (
                    <div key={l.name} className="flex justify-between text-xs mt-1.5 text-muted-foreground">
                      <span>{l.name}</span><span>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ─── CASHFLOW 12W ─── */}
          <TabsContent value="cashflow" className="space-y-4">
            <Card className="bg-card border-border/80 rounded-xl">
              <CardHeader>
                <CardTitle className="text-base">12-Week Rolling Cashflow Planner</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground border-b border-border">
                        <th className="text-left py-2 px-3">Week</th>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-right py-2 px-3">Inflow</th>
                        <th className="text-right py-2 px-3">Outflow</th>
                        <th className="text-right py-2 px-3">Net</th>
                        <th className="text-right py-2 px-3">Balance</th>
                        <th className="text-center py-2 px-3">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MOCK_CF.map(w => (
                        <tr key={w.wk} className={`border-b border-border/50 ${w.projected ? 'opacity-70' : ''}`}>
                          <td className="py-2 px-3 font-medium">{w.label}</td>
                          <td className="py-2 px-3 text-muted-foreground">{w.date}</td>
                          <td className="py-2 px-3 text-right text-emerald-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(w.inflow)}</td>
                          <td className="py-2 px-3 text-right text-red-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(w.outflow)}</td>
                          <td className={`py-2 px-3 text-right font-medium ${w.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: "'Space Grotesk', monospace" }}>
                            {w.net >= 0 ? '+' : ''}{fmt(w.net)}
                          </td>
                          <td className="py-2 px-3 text-right font-bold" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(w.balance)}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant="outline" className={`text-[10px] ${w.projected ? 'border-amber-500/30 text-amber-400' : 'border-emerald-500/30 text-emerald-400'}`}>
                              {w.projected ? 'Projected' : 'Actual'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── P&L ─── */}
          <TabsContent value="pnl" className="space-y-4">
            <Card className="bg-card border-border/80 rounded-xl">
              <CardHeader>
                <CardTitle className="text-base">Monthly P&L Statement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Revenue */}
                <div>
                  <div className="flex justify-between items-center py-2 border-b border-border font-semibold">
                    <span>Revenue</span>
                    <span className="text-emerald-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(MOCK_PNL.revenue.total)}</span>
                  </div>
                  {MOCK_PNL.revenue.lines.map(l => (
                    <div key={l.name} className="flex justify-between py-1.5 pl-4 text-sm text-muted-foreground">
                      <span>{l.name}</span><span style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </div>
                {/* COGS */}
                <div>
                  <div className="flex justify-between items-center py-2 border-b border-border font-semibold">
                    <span>Cost of Goods Sold</span>
                    <span className="text-red-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>({fmt(MOCK_PNL.cogs.total)})</span>
                  </div>
                  {MOCK_PNL.cogs.lines.map(l => (
                    <div key={l.name} className="flex justify-between py-1.5 pl-4 text-sm text-muted-foreground">
                      <span>{l.name}</span><span style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </div>
                {/* Gross Profit */}
                <div className="flex justify-between items-center py-3 border-y border-primary/30 font-bold text-lg">
                  <span>Gross Profit</span>
                  <span className="text-emerald-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(gp)}</span>
                </div>
                {/* OpEx */}
                <div>
                  <div className="flex justify-between items-center py-2 border-b border-border font-semibold">
                    <span>Operating Expenses</span>
                    <span className="text-red-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>({fmt(MOCK_PNL.opex.total)})</span>
                  </div>
                  {MOCK_PNL.opex.lines.map(l => (
                    <div key={l.name} className="flex justify-between py-1.5 pl-4 text-sm text-muted-foreground">
                      <span>{l.name}</span><span style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(l.amt)}</span>
                    </div>
                  ))}
                </div>
                {/* Net Income */}
                <div className="flex justify-between items-center py-3 border-y border-primary/30 font-bold text-lg">
                  <span>Net Income</span>
                  <span className={ni >= 0 ? 'text-emerald-400' : 'text-red-400'} style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(ni)}</span>
                </div>
                <p className="text-xs text-muted-foreground">Net margin: {((ni / MOCK_PNL.revenue.total) * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── DAILY P&L ─── */}
          <TabsContent value="daily" className="space-y-4">
            <Card className="bg-card border-border/80 rounded-xl">
              <CardHeader>
                <CardTitle className="text-base">Daily P&L (Last 10 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-right py-2 px-3">Revenue</th>
                      <th className="text-right py-2 px-3">COGS</th>
                      <th className="text-right py-2 px-3">OpEx</th>
                      <th className="text-right py-2 px-3">Net</th>
                      <th className="text-center py-2 px-3">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_DPL.map(d => {
                      const margin = ((d.net / d.revenue) * 100).toFixed(1);
                      const health = margin >= 30 ? 'emerald' : margin >= 20 ? 'amber' : 'red';
                      return (
                        <tr key={d.date} className="border-b border-border/50">
                          <td className="py-2 px-3 font-medium">{d.date}</td>
                          <td className="py-2 px-3 text-right text-emerald-400" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(d.revenue)}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(d.cogs)}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(d.opex)}</td>
                          <td className={`py-2 px-3 text-right font-medium text-${health}-400`} style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(d.net)}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant="outline" className={`text-[10px] border-${health}-500/30 text-${health}-400`}>
                              {margin}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── ACCOUNTS ─── */}
          <TabsContent value="accounts" className="space-y-4">
            <div className="grid gap-3">
              {MOCK_ACCOUNTS.map(a => (
                <Card key={a._id} className="bg-card border-border/80 rounded-xl">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${a.kind === 'checking' ? 'bg-blue-500/10' : a.kind === 'savings' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                        {a.kind === 'checking' ? <Building2 className="h-5 w-5 text-blue-400" /> :
                         a.kind === 'savings' ? <PiggyBank className="h-5 w-5 text-emerald-400" /> :
                         <CreditCard className="h-5 w-5 text-amber-400" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{a.name}</p>
                        <p className="text-xs text-muted-foreground">{a.bank} &middot; {a.kind}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${a.bal >= 0 ? 'text-foreground' : 'text-red-400'}`} style={{ fontFamily: "'Space Grotesk', monospace" }}>
                        {fmt(a.bal)}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {a.ok ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <AlertTriangle className="h-3 w-3 text-amber-400" />}
                        <span>Synced {a.sync}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setConnectModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Connect Another Account
            </Button>
          </TabsContent>

          {/* ─── INVENTORY FINANCING ─── */}
          <TabsContent value="inventory" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <KPI label="Total Inventory Value" value={fmt(MOCK_INV.total)} icon={Package} testId="kpi-inv-total" />
              <KPI label="Eligible for Financing" value={fmt(MOCK_INV.eligible)} sub={`${((MOCK_INV.eligible / MOCK_INV.total) * 100).toFixed(0)}% of total`} icon={Shield} testId="kpi-inv-eligible" />
              <KPI label="Avg Turnover Rate" value={`${MOCK_INV.turnover}x`} sub="Annual turns" icon={RefreshCw} testId="kpi-inv-turnover" />
            </div>
            <Card className="bg-card border-border/80 rounded-xl">
              <CardHeader>
                <CardTitle className="text-base">SKU-Level Financing Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-3">SKU</th>
                      <th className="text-left py-2 px-3">Name</th>
                      <th className="text-right py-2 px-3">Qty</th>
                      <th className="text-right py-2 px-3">Value</th>
                      <th className="text-right py-2 px-3">Turnover</th>
                      <th className="text-right py-2 px-3">Est. Credit (70% LTV)</th>
                      <th className="text-center py-2 px-3">Eligible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_INV.items.map(item => (
                      <tr key={item.sku} className="border-b border-border/50">
                        <td className="py-2 px-3 font-mono text-xs">{item.sku}</td>
                        <td className="py-2 px-3">{item.name}</td>
                        <td className="py-2 px-3 text-right" style={{ fontFamily: "'Space Grotesk', monospace" }}>{item.qty.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right" style={{ fontFamily: "'Space Grotesk', monospace" }}>{fmt(item.val)}</td>
                        <td className="py-2 px-3 text-right" style={{ fontFamily: "'Space Grotesk', monospace" }}>{item.turn}x</td>
                        <td className="py-2 px-3 text-right font-medium" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                          {item.ok ? fmt(item.val * 0.7) : '--'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {item.ok ? <CheckCircle className="h-4 w-4 text-emerald-400 mx-auto" /> : <AlertTriangle className="h-4 w-4 text-amber-400 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Connect Bank Modal */}
        <Dialog open={connectModal} onOpenChange={setConnectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect Bank Account</DialogTitle>
              <DialogDescription>
                Securely link your business bank accounts via Plaid. We use AES-256 encryption for all access tokens.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Connecting your bank enables automatic transaction sync, AI-powered categorization, and real-time cashflow forecasting.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['JPMorgan Chase', 'Bank of America', 'Wells Fargo', 'Mercury', 'Brex', 'Silicon Valley Bank'].map(bank => (
                  <Button key={bank} variant="outline" className="justify-start text-sm h-auto py-3" onClick={() => { toast.success(`Plaid Link would open for ${bank}`); setConnectModal(false); }}>
                    <Building2 className="h-4 w-4 mr-2 flex-shrink-0" />
                    {bank}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Powered by Plaid. Your credentials are never stored by Commerce Index.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
